/**
 * Keeping password managers off fields that are not credentials.
 *
 * Password managers do not read our markup the way we intend it. They score a
 * field on shape — a lone text box inside a `<form>`, an adjacent submit
 * button, a masked input — so the "New file" name box in the filesystem tab,
 * an inline rename, or a Sentry API token all get adopted as logins and grow a
 * vendor icon offering to fill or save a password.
 *
 * `autocomplete="off"` does not stop this. 1Password and LastPass deliberately
 * ignore it, treating it as a site trying to break their product, so the ~25
 * fields in this repo that already set it stayed broken. Each vendor ships its
 * own opt-out attribute instead, and those are what {@link
 * passwordManagerIgnoreProps} carries.
 */

/**
 * Spread onto any input that is not a credential to keep password managers
 * off it. The `<Input>` primitive applies this for you; reach for it directly
 * only on a raw `<input>`.
 *
 * ```tsx
 * <input value={draft} onChange={...} {...passwordManagerIgnoreProps} />
 * ```
 */
export const passwordManagerIgnoreProps = {
  /** Browsers. Ignored by the extensions below, which is the whole problem. */
  autoComplete: 'off',
  /** 1Password: skip this field entirely. Presence is what counts. */
  'data-1p-ignore': '',
  /** LastPass. */
  'data-lpignore': 'true',
  /** Dashlane: `other` means "not a credential field". */
  'data-form-type': 'other',
} as const;

/** The props {@link resolvePasswordManagerProps} spreads onto a field. */
export interface PasswordManagerProps {
  autoComplete?: string;
  'data-1p-ignore'?: string;
  'data-lpignore'?: string;
  'data-form-type'?: string;
}

/**
 * Override the inference for one field. `allow` for a credential input a
 * password manager should see but that cannot name itself with `autocomplete`;
 * `ignore` for one that names itself but should still be left alone.
 */
export type PasswordManagerBehavior = 'allow' | 'ignore';

/** `autocomplete` values that disable autofill rather than describe a field. */
const NO_AUTOFILL_TOKENS = new Set(['off', 'none']);

/**
 * Whether a field has told the platform what it holds.
 *
 * `autocomplete` is the web's mechanism for exactly this, so we take it at its
 * word: a field that names itself (`current-password`, `email`, `name`,
 * `one-time-code`) wants autofill, and a field that says nothing does not.
 * Deliberately not keyed on `type="password"` — that declares *masking*, not
 * credentials. Nearly every masked input in this app is an API key or a
 * webhook secret, and six of them flip between `text` and `password` behind a
 * reveal toggle, which would otherwise make a field enter and leave password
 * manager adoption as the user clicks the eye.
 */
export function declaresAutofillIntent(autoComplete: string | undefined): boolean {
  if (!autoComplete) return false;
  const tokens = autoComplete.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return tokens.length > 0 && !tokens.some((token) => NO_AUTOFILL_TOKENS.has(token));
}

/**
 * Resolve the password-manager props for a field, from an explicit `behavior`
 * when the caller passed one and from the field's own `autocomplete` otherwise.
 * Returns an empty object for credential fields so the manager stays useful
 * where it belongs — sign-in, registration, first-run setup.
 */
export function resolvePasswordManagerProps({
  autoComplete,
  behavior,
}: {
  autoComplete?: string;
  behavior?: PasswordManagerBehavior;
}): PasswordManagerProps {
  const allow = behavior ? behavior === 'allow' : declaresAutofillIntent(autoComplete);
  return allow ? {} : passwordManagerIgnoreProps;
}
